<template>
    <div class="documents-menu">
        <div class="documents-menu__header">
            Categories
        </div>
        <ul class="documents-menu__list">
            <li 
                class="documents-menu__item"
                :class="{ 'documents-menu__item--active': selectedCategory === '' }"
                @click="selectCategory('')"
            >
                <div class="documents-menu__item-content">
                    <div class="documents-menu__item-label">All Documents</div>
                    <div class="documents-menu__item-count">{{ allDocuments.length }}</div>
                </div>
            </li>
            
            <li 
                v-for="category in categoriesWithCounts" 
                :key="category.id"
                class="documents-menu__item"
                :class="{ 'documents-menu__item--active': selectedCategory === category.id }"
            >
                <div 
                    class="documents-menu__item-content"
                    @click="toggleCategory(category.id)"
                >
                    <div class="documents-menu__item-label">{{ category.name }}</div>
                    <div class="documents-menu__item-count">{{ category.document_count }}</div>
                </div>
                
                <transition name="fade">
                    <ul v-if="categoryVisible(category.id)" class="documents-menu__subitems">
                        <li 
                            v-for="subcategory in getSubcategoriesByCategory(category.id)" 
                            :key="subcategory.id"
                            class="documents-menu__subitem"
                            :class="{ 'documents-menu__subitem--active': selectedSubCategory === subcategory.id }"
                            @click.stop="selectSubcategory(subcategory.id)"
                        >
                            <div class="documents-menu__subitem-content">
                                <span class="documents-menu__subitem-label">{{ subcategory.name }}</span>
                                <span class="documents-menu__subitem-count">{{ subcategory.document_count }}</span>
                            </div>
                        </li>
                    </ul>
                </transition>
            </li>
        </ul>
    </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
    name: 'DocumentsMenuV1',
    
    props: {
        documentData: {
            type: Array,
            default: () => []
        },
        showCategory: {
            type: Number,
            required: false
        },
        showSubcategory: {
            type: Number,
            required: false
        }
    },

    data() {
        return {
            selectedCategory: this.showCategory || '',
            selectedSubCategory: this.showSubcategory || '',
            visibleCategories: {}
        };
    },

    created() {
        this.$store.dispatch('loadCategories');
    },

    mounted() {
        if (this.documentData && this.documentData.length) {
            this.$store.commit('updateDocuments', { type: 'all', items: this.documentData });
        }

        // Auto-expand the selected category if provided
        if (this.selectedCategory) {
            this.$set(this.visibleCategories, this.selectedCategory, true);
        }
    },

    computed: {
        ...mapState(['documents']),

        allDocuments() {
            if (this.documentData && this.documentData.length) {
                return this.documentData;
            }
            return this.documents.all || [];
        },

        availableCategories() {
            return this.documents.categories || [];
        },

        availableSubcategories() {
            return this.documents.subcategories || [];
        },

        categoriesWithCounts() {
            return this.availableCategories.map(category => ({
                ...category,
                document_count: this.getDocumentsByCategory(category.id).length
            })).filter(cat => cat.document_count > 0);
        }
    },

    methods: {
        selectCategory(categoryId) {
            // Toggle the category visibility
            const isCurrentlyVisible = this.visibleCategories[categoryId];
            this.$set(this.visibleCategories, categoryId, !isCurrentlyVisible);
            
            // Set as selected category
            this.selectedCategory = categoryId;
            this.selectedSubCategory = '';
            
            // Emit via event hub for cross-component communication
            this.$eventHub.$emit('documents-menu-filter-changed', { 
                category: categoryId, 
                subcategory: '' 
            });
        },

        selectSubcategory(subcategoryId) {
            this.selectedSubCategory = subcategoryId;
            
            // Emit via event hub for cross-component communication
            this.$eventHub.$emit('documents-menu-filter-changed', { 
                category: this.selectedCategory, 
                subcategory: subcategoryId 
            });
        },

        toggleCategory(categoryId) {
            this.$set(this.visibleCategories, categoryId, !this.visibleCategories[categoryId]);
        },

        categoryVisible(categoryId) {
            return this.visibleCategories[categoryId] || false;
        },

        getDocumentsByCategory(categoryId) {
            return this.allDocuments.filter(doc => doc.category_id === categoryId);
        },

        getSubcategoriesByCategory(categoryId) {
            return this.availableSubcategories
                .filter(sub => sub.category_id === categoryId)
                .map(sub => ({
                    ...sub,
                    document_count: this.allDocuments.filter(doc => doc.subcategory_id === sub.id).length
                }))
                .filter(sub => sub.document_count > 0);
        }
    }
};
</script>
